import { Button, Group, Menu } from '@mantine/core';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { VideoTutorialButton } from 'src/Components/Shared/VideoTutorialButton';
import { useCreateContactRuleExport } from 'src/Hooks/Mutations/useCreateContactRuleExport';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useCanCreate, useCanRead } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { CreateEntityExportDtoRuleEnum } from 'src/Services/API/generated';
import { contactExportNameGenerator } from 'src/Services/Formatters/contactExportNameGenerator';

import AssetContactList from '../../../Components/Contacts/AssetContactList/AssetContactList.component';

import styles from './ContactsListPage.module.scss';

const ContactsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [exportName, setExportName] = useState<string>('');

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('contact');
  const { hasPermission: canCreate } = useCanCreate('contact');
  const { hasPermission: canExport } = useCanCreate('entityExport');

  const { mutate, data, isLoading } = useCreateContactRuleExport({
    onSuccess: data => {
      setShowQRCodeModal(true);
      setExportName(contactExportNameGenerator(data, t));
    },
  });

  const [showQRCodeModal, setShowQRCodeModal] = useState(false);

  const { data: asset } = useFetchAssetDetails();

  const createContact = useCallback(() => {
    if (!asset) return;

    navigate('new');
  }, [asset, navigate]);

  const assetAboveQuotas = useIsAssetAboveQuotas();

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  return (
    <div className={styles.contactsPageContainer}>
      <QRCodeModal
        open={showQRCodeModal}
        url={data?.publicUrl ?? ''}
        onClose={() => setShowQRCodeModal(false)}
        exportName={exportName}
      />
      <div className={styles.topLine}>
        <div className={styles.titleColumn}>
          <div className={styles.title}>{t('contacts.contacts', 'Contacts')}</div>
          <div className={styles.subtitle}>
            {t(
              'contacts.subtitle',
              'Retrouvez ici tous les contacts utiles pour la gestion commune du bien'
            )}
          </div>
        </div>
        <Group>
          <VideoTutorialButton videoUrl={'https://youtu.be/GjjDWx6m0bU'} />
          {canExport && (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Button
                  variant={'outline'}
                  rightSection={<IconChevronDown height={20} />}
                  loading={isLoading}
                >
                  {t('contacts.export', 'Exporter')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => mutate(CreateEntityExportDtoRuleEnum.Emergency)}>
                  Contacts d'urgence
                </Menu.Item>
                <Menu.Item onClick={() => mutate(CreateEntityExportDtoRuleEnum.All)}>
                  Tous les contacts
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
          {canCreate && (
            <Button
              onClick={() => createContact()}
              variant="primary"
              leftSection={<IconPlus size={20} />}
              disabled={assetAboveQuotas}
            >
              {t('contacts.addContact', 'Ajouter un contact')}
            </Button>
          )}
        </Group>
      </div>
      <div className={styles.cardContainer}>
        <AssetContactList />
      </div>
      <Outlet />
    </div>
  );
};

export default ContactsListPage;
